import React, { useState, useMemo } from "react"
import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis,
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from "recharts"
import {
  Target, BookOpen, Award, Zap, FileText,
  TrendingUp, AlertTriangle, ChevronRight, Search,
} from "lucide-react"
import { Sidebar } from "@/components/ui/sidebar"
import { UniversalSearch } from "@/components/ui/universal-search"
import ProtectedRoute from "@/components/ProtectedRoute"
import { useStudentSummary } from "@/hooks/useStudentSummary"
import { useStudentAnalytics } from "@/hooks/useStudentAnalytics"
import { generateReport } from "@/hooks/useReportGenerator"
import { ReportModal } from "@/components/ui/report/report-modal"
import { AuthStore } from "@/hooks/stores/AuthStore"
import { StorageService } from "@/hooks/services/storageService"
import { getAllNodes } from "@/data/career-tree"

// ─── Shared helpers ───────────────────────────────────────────────────────────

function StatCard({ label, value, icon: Icon, color }: {
  label: string; value: string | number; icon: React.ElementType; color: string
}) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 flex items-center gap-4">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${color}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <p className="text-2xl font-black text-slate-900 leading-none">{value}</p>
        <p className="text-xs font-semibold text-slate-500 mt-0.5">{label}</p>
      </div>
    </div>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
      <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wide mb-4">{title}</h3>
      {children}
    </div>
  )
}

// ─── Skill Gap helpers ────────────────────────────────────────────────────────

function getStatus(studentScore: number, threshold: number) {
  const ratio = studentScore / threshold
  if (ratio >= 0.9) return "strong"
  if (ratio >= 0.65) return "close"
  return "gap"
}

const STATUS_CONFIG = {
  strong: { label: "Strong", bar: "bg-emerald-500", text: "text-emerald-700", bg: "bg-emerald-50 border-emerald-200" },
  close:  { label: "Close",  bar: "bg-amber-400",   text: "text-amber-700",  bg: "bg-amber-50 border-amber-200"  },
  gap:    { label: "Gap",    bar: "bg-red-400",      text: "text-red-700",   bg: "bg-red-50 border-red-200"      },
}

const ACTION_MAP: Record<string, (trait: string) => string> = {
  strong: (t) => `Keep reinforcing ${t} through regular practice and challenge activities.`,
  close:  (t) => `You're nearly there on ${t}. Focus one week of study sessions on this trait.`,
  gap:    (t) => `${t} needs the most work. Look for Journey modules and games that build this skill.`,
}

function TraitRow({ trait, studentScore, threshold }: { trait: any; studentScore: number; threshold: number }) {
  const status = getStatus(studentScore, threshold)
  const cfg = STATUS_CONFIG[status]
  const pct = Math.min(100, Math.round((studentScore / threshold) * 100))
  return (
    <div className={`rounded-xl border p-4 ${cfg.bg}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-bold text-slate-800">{trait.name}</span>
        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${cfg.text} bg-white/60`}>{cfg.label}</span>
      </div>
      <div className="flex items-center gap-3 mb-2">
        <div className="flex-1 h-2.5 bg-white/70 rounded-full overflow-hidden">
          <div className={`h-full rounded-full transition-all duration-500 ${cfg.bar}`} style={{ width: `${pct}%` }} />
        </div>
        <span className="text-xs font-bold text-slate-600 w-20 text-right shrink-0">
          {studentScore} / {threshold}
        </span>
      </div>
      <p className="text-xs text-slate-500 leading-relaxed">{ACTION_MAP[status](trait.name)}</p>
    </div>
  )
}

// ─── Skill Gap tab ────────────────────────────────────────────────────────────

function SkillGapTab() {
  const [search, setSearch] = useState("")
  const [selectedId, setSelectedId] = useState("")

  const { traitProfile } = useMemo(() => {
    const currentUser = AuthStore.getState().currentUser
    const students = StorageService.read("students") ?? []
    const student = students.find((s: any) => s.userId === currentUser?._id || s.email === currentUser?.email)
    return { traitProfile: student?.traitProfile ?? null }
  }, [])

  const fieldNodes = useMemo(() =>
    getAllNodes().filter((n: any) => n.level === "field" && n.traits?.length > 0)
  , [])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return fieldNodes
    return fieldNodes.filter((n: any) =>
      n.name.toLowerCase().includes(q) || n.broadCategory?.toLowerCase().includes(q)
    )
  }, [fieldNodes, search])

  const selected = fieldNodes.find((n: any) => n.nodeId === selectedId) ?? filtered[0] ?? null

  const analysis = useMemo(() => {
    if (!selected || !traitProfile) return null
    const traits = selected.traits.map((tr: any) => ({
      ...tr,
      studentScore: traitProfile[tr.traitId] ?? 0,
      status: getStatus(traitProfile[tr.traitId] ?? 0, tr.minimumThreshold),
    }))
    const strong = traits.filter((t: any) => t.status === "strong").length
    const close  = traits.filter((t: any) => t.status === "close").length
    const gaps   = traits.filter((t: any) => t.status === "gap").length
    const overall = Math.round((strong / traits.length) * 100)
    return { traits, strong, close, gaps, overall }
  }, [selected, traitProfile])

  return (
    <div className="space-y-6">
      {!traitProfile && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0" />
          <p className="text-sm text-amber-800 font-medium">
            Complete the Psychometric Assessment first to see your personalised skill gaps.
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Career picker */}
        <div className="lg:col-span-4 space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search career…"
              className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
            />
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden max-h-130 overflow-y-auto">
            {filtered.map((n: any) => (
              <button
                key={n.nodeId}
                onClick={() => setSelectedId(n.nodeId)}
                className={`w-full text-left px-4 py-3 border-b border-slate-50 last:border-0 flex items-center gap-3 transition-colors ${
                  (selectedId === n.nodeId || (!selectedId && n.nodeId === filtered[0]?.nodeId))
                    ? "bg-indigo-50"
                    : "hover:bg-slate-50"
                }`}
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-800 truncate">{n.name}</p>
                  <p className="text-xs text-slate-400">{n.broadCategory}</p>
                </div>
                <ChevronRight className="w-4 h-4 text-slate-300 shrink-0" />
              </button>
            ))}
            {filtered.length === 0 && (
              <p className="text-center py-8 text-sm text-slate-400">No careers found</p>
            )}
          </div>
        </div>

        {/* Analysis panel */}
        <div className="lg:col-span-8 space-y-5">
          {selected && (
            <>
              <div className="bg-white rounded-2xl border border-slate-200 p-5">
                <h2 className="text-xl font-black text-slate-900">{selected.name}</h2>
                <p className="text-sm text-slate-500 mt-1">{selected.description}</p>

                {analysis && (
                  <div className="mt-4 grid grid-cols-3 gap-3">
                    <div className="text-center bg-emerald-50 rounded-xl p-3 border border-emerald-100">
                      <p className="text-2xl font-black text-emerald-600">{analysis.strong}</p>
                      <p className="text-xs text-emerald-600 font-semibold mt-0.5">Strong</p>
                    </div>
                    <div className="text-center bg-amber-50 rounded-xl p-3 border border-amber-100">
                      <p className="text-2xl font-black text-amber-600">{analysis.close}</p>
                      <p className="text-xs text-amber-600 font-semibold mt-0.5">Close</p>
                    </div>
                    <div className="text-center bg-red-50 rounded-xl p-3 border border-red-100">
                      <p className="text-2xl font-black text-red-600">{analysis.gaps}</p>
                      <p className="text-xs text-red-600 font-semibold mt-0.5">Gap</p>
                    </div>
                  </div>
                )}

                {analysis && (
                  <div className="mt-4">
                    <div className="flex justify-between text-xs text-slate-500 mb-1">
                      <span className="font-semibold">Overall readiness</span>
                      <span className="font-bold text-indigo-600">{analysis.overall}%</span>
                    </div>
                    <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full bg-linear-to-r from-indigo-500 to-violet-500 transition-all duration-700"
                        style={{ width: `${analysis.overall}%` }}
                      />
                    </div>
                  </div>
                )}

                {!traitProfile && (
                  <p className="mt-4 text-sm text-slate-400 italic">Complete the assessment to see your scores.</p>
                )}
              </div>

              {analysis && (
                <div className="space-y-3">
                  <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wide">Trait Breakdown</h3>
                  {analysis.traits
                    .sort((a: any, b: any) => {
                      const order: Record<string, number> = { gap: 0, close: 1, strong: 2 }
                      return order[a.status] - order[b.status]
                    })
                    .map((tr: any) => (
                      <TraitRow
                        key={tr.traitId}
                        trait={tr}
                        studentScore={tr.studentScore}
                        threshold={tr.minimumThreshold}
                      />
                    ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

type Tab = "overview" | "skill-gap"

export default function StudentInsightsPage() {
  const user = AuthStore.getState().currentUser
  const summary = useStudentSummary()
  const analytics = useStudentAnalytics(user?._id ?? "")
  const [report, setReport] = useState<ReturnType<typeof generateReport> | null>(null)
  const [activeTab, setActiveTab] = useState<Tab>("overview")

  const radarData = summary?.topTraits.map((t) => ({ subject: t.name, score: t.score })) ?? []
  const hasTraitData = radarData.length > 0

  const handleGenerateReport = () => {
    if (!summary) return
    setReport(generateReport(summary, analytics, 'student'))
  }

  const tabs: { id: Tab; label: string }[] = [
    { id: "overview",   label: "Overview"   },
    { id: "skill-gap",  label: "Skill Gap"  },
  ]

  return (
    <ProtectedRoute>
      <div className="flex min-h-screen bg-slate-100">
        <Sidebar />
        <UniversalSearch onSearch={() => {}} />

        <main className="flex-1 lg:ml-64 p-4 md:p-6 overflow-y-auto">
          <div className="max-w-6xl mx-auto space-y-5">

            {/* Page header */}
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <h1 className="text-2xl font-black text-slate-900">My Insights</h1>
                <p className="text-sm text-slate-500 mt-0.5">
                  Your learning journey at a glance — traits, progress, and goals.
                </p>
              </div>
              {activeTab === "overview" && (
                <button
                  onClick={handleGenerateReport}
                  disabled={!summary}
                  className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40 text-white text-sm font-bold px-4 py-2.5 rounded-xl transition-all active:scale-95 shadow-sm shrink-0"
                >
                  <FileText className="w-4 h-4" />
                  Generate Report
                </button>
              )}
            </div>

            {/* Tabs */}
            <div className="flex gap-1 bg-white border border-slate-200 rounded-xl p-1 w-fit shadow-sm">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-5 py-2 rounded-lg text-sm font-semibold transition-all ${
                    activeTab === tab.id
                      ? "bg-indigo-600 text-white shadow-sm"
                      : "text-slate-500 hover:text-slate-700 hover:bg-slate-50"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Overview tab */}
            {activeTab === "overview" && (
              <>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  <StatCard
                    label="Topics Completed"
                    value={analytics.careerPathProgress.reduce((s, p) => s + Math.round(p.progress / 10), 0)}
                    icon={BookOpen}
                    color="bg-indigo-100 text-indigo-600"
                  />
                  <StatCard
                    label="Active Goals"
                    value={analytics.goalProgress.active}
                    icon={Target}
                    color="bg-teal-100 text-teal-600"
                  />
                  <StatCard
                    label="Goals Completed"
                    value={analytics.goalProgress.completed}
                    icon={Award}
                    color="bg-amber-100 text-amber-600"
                  />
                  <StatCard
                    label="Study Sessions"
                    value={analytics.engagementTrend.reduce((s, e) => s + e.sessions, 0)}
                    icon={Zap}
                    color="bg-violet-100 text-violet-600"
                  />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                  <Section title="Trait Profile">
                    {hasTraitData ? (
                      <ResponsiveContainer width="100%" height={260}>
                        <RadarChart data={radarData}>
                          <PolarGrid stroke="#e2e8f0" />
                          <PolarAngleAxis dataKey="subject" tick={{ fontSize: 11, fill: '#64748b' }} />
                          <Radar name="Score" dataKey="score" stroke="#6366f1" fill="#6366f1" fillOpacity={0.25} strokeWidth={2} />
                        </RadarChart>
                      </ResponsiveContainer>
                    ) : (
                      <p className="text-sm text-slate-400 text-center py-10">Complete the psychometric assessment to see your trait profile.</p>
                    )}
                  </Section>

                  <Section title="Activity Breakdown">
                    {analytics.activityBreakdown.length > 0 ? (
                      <ResponsiveContainer width="100%" height={260}>
                        <PieChart>
                          <Pie data={analytics.activityBreakdown} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false}>
                            {analytics.activityBreakdown.map((entry, i) => (
                              <Cell key={i} fill={entry.fill} />
                            ))}
                          </Pie>
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : (
                      <p className="text-sm text-slate-400 text-center py-10">No activity logged yet. Start a lesson to see your breakdown.</p>
                    )}
                  </Section>

                  <Section title="Weekly XP Earned">
                    {analytics.weeklyXP.length > 0 ? (
                      <ResponsiveContainer width="100%" height={200}>
                        <BarChart data={analytics.weeklyXP} barSize={24}>
                          <XAxis dataKey="week" tick={{ fontSize: 11, fill: '#94a3b8' }} />
                          <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} />
                          <Tooltip />
                          <Bar dataKey="xp" fill="#818cf8" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <p className="text-sm text-slate-400 text-center py-10">XP data will appear as you complete activities.</p>
                    )}
                  </Section>

                  <Section title="Subject Performance">
                    {analytics.subjectStrengths.length > 0 ? (
                      <div className="space-y-3">
                        {analytics.subjectStrengths.map((s) => (
                          <div key={s.subject}>
                            <div className="flex items-center justify-between text-sm mb-1">
                              <span className="font-semibold text-slate-700">{s.subject}</span>
                              <span className="font-bold text-slate-500">{s.score}%</span>
                            </div>
                            <div className="w-full bg-slate-100 rounded-full h-2">
                              <div
                                className="bg-linear-to-r from-teal-400 to-indigo-500 h-2 rounded-full transition-all duration-500"
                                style={{ width: `${s.score}%` }}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-slate-400 text-center py-10">Complete quizzes to see your subject performance.</p>
                    )}
                  </Section>
                </div>

                {analytics.careerPathProgress.length > 0 && (
                  <Section title="Career Path Progress">
                    <div className="space-y-3">
                      {analytics.careerPathProgress.map((p) => (
                        <div key={p.pathId}>
                          <div className="flex items-center justify-between text-sm mb-1">
                            <span className="font-semibold text-slate-700">{p.pathName}</span>
                            <span className="font-bold text-indigo-600">{p.progress}%</span>
                          </div>
                          <div className="w-full bg-slate-100 rounded-full h-2.5">
                            <div
                              className="bg-linear-to-r from-indigo-500 to-violet-500 h-2.5 rounded-full transition-all duration-500"
                              style={{ width: `${p.progress}%` }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </Section>
                )}

                {summary && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <Section title="Your Strengths">
                      <ul className="space-y-2">
                        {summary.strengths.map((s) => (
                          <li key={s} className="flex items-center gap-2 text-sm text-slate-700">
                            <span className="w-2 h-2 rounded-full bg-teal-400 shrink-0" />
                            {s}
                          </li>
                        ))}
                        {summary.strengths.length === 0 && (
                          <li className="text-sm text-slate-400">Complete more activities to reveal your strengths.</li>
                        )}
                      </ul>
                    </Section>
                    <Section title="Areas to Grow">
                      <ul className="space-y-2">
                        {summary.areasForImprovement.map((s) => (
                          <li key={s} className="flex items-center gap-2 text-sm text-slate-700">
                            <span className="w-2 h-2 rounded-full bg-amber-400 shrink-0" />
                            {s}
                          </li>
                        ))}
                        {summary.areasForImprovement.length === 0 && (
                          <li className="text-sm text-slate-400">No weak areas identified yet — keep going!</li>
                        )}
                      </ul>
                    </Section>
                  </div>
                )}
              </>
            )}

            {/* Skill Gap tab */}
            {activeTab === "skill-gap" && <SkillGapTab />}

          </div>
        </main>
      </div>

      {report && <ReportModal report={report} onClose={() => setReport(null)} />}
    </ProtectedRoute>
  )
}
